<template>
  <v-app id="app" :style="appStyles">
    <transition name="fadeRouter" mode="out-in">
      <v-main>
        <v-responsive
          min-height="100vh"
          max-height="100vh"
          style="overflow: auto"
        >
          <transition name="fadeRouter" mode="out-in">
            <router-view />
          </transition>
        </v-responsive>
      </v-main>
    </transition>

    <AzureLoginErrorDialog
      ref="azureLoginErrorDialogRef"
      @hide="closeAzureLoginErrorDialog"
    />
  </v-app>
</template>

<script>
import configs from '@/configs.js'
import pkg from '../package.json'

const version = pkg.version

import { mapActions, mapGetters, mapMutations } from 'vuex'
import AzureLoginErrorDialog from '@/components/Login/AzureLoginErrorDialog'
import buildBackgroundUrl from '@/helpers/buildBackgroundUrl'
import { readSharedHash } from '@/helpers/sharedSession'

const themeModules = import.meta.glob('@/plugins/themes/*.js', { eager: true })

export default {
    components: {
        AzureLoginErrorDialog,
    },

    computed: {
        ...mapGetters('auth', ['getHash', 'getTokenExp']),
        ...mapGetters('theme', ['getTheme']),

        appStyles() {
            return {
                '-moz-background-size': 'cover',
                '-o-background-size': 'cover',
                '-webkit-background-size': 'cover',
                background: `${this.$vuetify.theme.themes.light.primary}`,
                'background-image': `${this.bgUrl}`,
                'background-position': 'center',
                'background-repeat': 'no-repeat',
                'background-size': 'cover',
            }
        },

        bgUrl() {
            return buildBackgroundUrl(configs.system)
        },
    },

    async created() {
        this.setLoadingAuth(true)
        this.setTheme()

        this.verifyAppVersion()

        this.setEnableSessionExpiredDialog(false)
        this.setSessionExpiredDialog(false)
        let url = new URL(window.location.href)
        let hash = null
        let hashUrl = false
        let hashStore = false
        let isValidSessionToken = false
        let isValidHash = false
        let isLogout = false

        const errorCode = !!url.searchParams.get('errorCode')

        if (errorCode) {
            this.$nextTick(() => {
                return this.showAzureLoginErrorDialog({
                    code: errorCode,
                    message: url.searchParams.get('errorMessage'),
                })
            })
        }

        isLogout = !!url.searchParams.get('logout')

        const urlHash = url.searchParams.get('token')
        const sharedHash = readSharedHash()

        if (!urlHash && !sharedHash && this.getHash) this.resetState()

        const entryHash = urlHash || (sharedHash && sharedHash !== this.getHash ? sharedHash : null)

        hashUrl = !!entryHash

        if (hashUrl) {
            hash = entryHash
        } else {
            hash = this.getHash

            if (hash) {
                hashStore = true
            }
        }

        if (isLogout) {
            this.logout()
        }

        if (hash) {
            isValidHash = await this.isValidHash({ hash })
        }

        let tokenExp = this.getTokenExp
        if (tokenExp) {
            isValidSessionToken = this.isValidToken(tokenExp)
        }

        if (!hashUrl && !hashStore && !isValidSessionToken && !isValidHash) {
            console.log('entrei aqui ne?')

            this.resetState()
            return this.$router.push('/')
        }

        if (hashUrl && isValidHash) {
            const auth = await this.hashAuthenticate(hash)
            if (auth === 'success') {
                if (this.$route.path !== '/modules') {
                    this.setInvalidHashDialog(false)
                    this.setEnableSessionExpiredDialog(true)
                }
                return this.successLogin(url.searchParams.get('route'))
            } else {
                let err = auth
                this.$fnError(err)
            }
        }

        if (hashStore && isValidHash && !isValidSessionToken) {
            return this.setSessionExpiredDialog(true)
        }

        if ((hashUrl || hashStore) && !isValidHash && !isValidSessionToken) {
            this.resetState()
            return this.$router.push('/')
        }

        if (!isValidHash && isValidSessionToken) {
            const auth = await this.getHashAuth()
            if (auth === 'success') {
                let route = url.searchParams.get('route')
                return this.successLogin(route)
            } else {
                return
            }
        }

        const route = url.searchParams.get('route')

        if (route) return this.successLogin(route)

        this.setLoadingAuth(false)
        this.setEnableSessionExpiredDialog(true)
    },

    data() {
        return {
            appVersion: version,
        }
    },

    methods: {
        ...mapActions('auth', ['logout', 'hashAuthenticate', 'getHashAuth']),
        ...mapMutations('auth', [
            'setEnableSessionExpiredDialog',
            'setSessionExpiredDialog',
            'resetState',
            'setLoadingAuth',
            'setReloadDialog',
            'setInvalidHashDialog',
        ]),

        closeAzureLoginErrorDialog() {
            this.$refs.azureLoginErrorDialogRef.hide()
        },

        async isValidHash(payload) {
            if (!payload.hash) {
                return this.logout()
            }

            try {
                const res = await this.$http.post(`${this.$ipUser}user/validate-hash`, {
                    ...payload,
                })
                if (res) {
                    if (res.data.isValidHash === null) {
                        this.resetState()
                        return this.$router.push('/')
                    }

                    return !!res.data.isValidHash
                }
            } catch (err) {
                this.$fnError(err)
                return false
            }
        },

        isValidToken(tokenExp) {
            let now = Math.floor(Date.now() / 1000)
            return now - tokenExp <= 0
        },

        removeHashFromURL() {
            window.history.replaceState({}, document.title, `${import.meta.env.BASE_URL}modules`)
        },

        setTheme() {
            const matchKey = Object.keys(themeModules).find((key) =>
                key.endsWith(`/${this.getTheme}`),
            )
            if (matchKey) {
                const colours = themeModules[matchKey]
                this.$vuetify.theme.themes.light = colours.default
            }
        },

        showAzureLoginErrorDialog(errorData) {
            this.$refs.azureLoginErrorDialogRef.show(errorData)
        },

        successLogin(route = null) {
            this.setEnableSessionExpiredDialog(true)
            this.setLoadingAuth(false)

            if (route) return this.$router.push(`/${route}`)
            if (this.$route.path === '/') return this.$router.push('/modules')

            return this.removeHashFromURL()
        },

        async verifyAppVersion() {
            if (!import.meta.env.DEV) {
                let serverVersion = ''

                fetch(`${import.meta.env.BASE_URL}version.json`)
                    .then((response) => response.json())
                    .then((data) => {
                        serverVersion = data.version
                        if (this.appVersion !== serverVersion) {
                            console.log('serverVersion: ', serverVersion)
                            console.log('appVersion: ', this.appVersion)
                            return this.setReloadDialog(true)
                        } else {
                            return this.setReloadDialog(false)
                        }
                    })
                    .catch((err) => {
                        this.$fnError(err)
                        this.setReloadDialog(false)
                    })
            }
        },
    },
    name: 'App',
}
</script>

<style>
html {
  overflow-y: auto !important;
  font-family: 'Roboto';
}

/* ::-webkit-scrollbar-track{
	display: none;
}

::-webkit-scrollbar{
	width: 8px;
	z-index:200;
	height: 5px;
}

::-webkit-scrollbar-thumb{
	background-color: rgba(0,0,0,0.3);
	border-radius: 10px;
} */

.fadeRouter-enter {
  opacity: 0;
}

.fadeRouter-enter-active {
  transition: opacity 0.28s ease;
}

.fadeRouter-leave-active {
  transition: opacity 0.28s ease;
  opacity: 0;
}

</style>
